import { ThemeProvider } from '@mui/material';
import { useEffect, useLayoutEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { useIsMounted } from 'usehooks-ts';
import { APIProvider } from './contexts/API/APIContext';
import { CalendarProvider } from './contexts/CalendarContext';
import { GlobalProvider } from './contexts/GlobalContext';
import { MissionProvider } from './contexts/MissionContext';
import { ParametersProvider } from './contexts/ParametersContext';
import { ScoreProvider } from './contexts/ScoreContext';
import { UIProvider } from './contexts/UIContext';
import { UserProvider } from './contexts/UserContext';
import AppRouter from './router/AppRouter';
import { theme } from './style/Theme';
import axios from 'axios';

// Root app wrapper: keeps auth token alive and composes global providers.
function App() {
	const isMounted = useIsMounted();
	let authInterval: ReturnType<typeof setInterval>;

	// Triggers backend token refresh endpoint to keep session/auth material warm.
	const acquireToken = async () => {
		try {
			await axios.get('/auth/sso/acquireToken');
		} catch (error: any) {
			if (axios.isAxiosError(error) && error.response?.data) {
				console.log(error.response.data);
			} else {
				console.log('An unexpected error occurred');
			}
		}
	};
	function authTimer() {
		// 58 minutes stays below 1h token windows and avoids synchronized bursts.
		authInterval = setInterval(() => {
			// Wrap the async call in a synchronous function
			void (async () => {
				const getToken = await acquireToken();
				console.log(getToken);
			})();
		}, 3480000); // run every 58 mins
	}

	useLayoutEffect(() => {
		// Run an immediate refresh once on mount so first protected calls have a valid token.
		async function initiateToken() {
			const getToken = await acquireToken();
			console.log(getToken);
		}
		void initiateToken();
	}, []);

	useEffect(() => {
		// Keep refreshing in background and release interval on unmount.
		authTimer();
		return () => {
			clearInterval(authInterval);
		};
	}, []);

	useEffect(() => {
		if (!isMounted()) return;
	}, [isMounted]);

	return (
		<BrowserRouter>
			{/* Provider order matters because downstream contexts depend on upstream values. */}
			<ThemeProvider theme={theme}>
				<APIProvider>
					<GlobalProvider>
						<UIProvider>
							<ParametersProvider>
								<MissionProvider>
									<CalendarProvider>
										<UserProvider>
											<ScoreProvider>
												<AppRouter />
											</ScoreProvider>
										</UserProvider>
									</CalendarProvider>
								</MissionProvider>
							</ParametersProvider>
						</UIProvider>
					</GlobalProvider>
				</APIProvider>
			</ThemeProvider>
		</BrowserRouter>
	);
}

export default App;
